import type { ThreadNgPreset } from "@/features/thread/stores/threadNgPresetStorage";
import { validateThreadNgWords } from "@/features/thread/utils/threadNgWords";
import {
  createMediator,
  type Mediator,
  type TypedEvent,
} from "@/shared/lib/componentHierarchy";

export interface ThreadNgWordEditorState {
  readonly value: string;
  readonly validation: ReturnType<typeof validateThreadNgWords>;
  readonly disabled: boolean;
  readonly presets: readonly ThreadNgPreset[];
  readonly name: string;
  readonly selectedName: string;
  readonly storageMessage: string;
}

export type ThreadNgWordEditorEvent =
  | {
      readonly type: "thread-ng-editor/model-synchronized";
      readonly value: string;
      readonly disabled: boolean;
    }
  | {
      readonly type: "thread-ng-editor/presets-synchronized";
      readonly presets: ThreadNgPreset[];
    }
  | { readonly type: "thread-ng-editor/value-changed"; readonly value: string }
  | { readonly type: "thread-ng-editor/name-changed"; readonly name: string }
  | {
      readonly type: "thread-ng-editor/selection-changed";
      readonly name: string;
    }
  | { readonly type: "thread-ng-editor/save-requested" }
  | { readonly type: "thread-ng-editor/delete-requested" }
  | { readonly type: "thread-ng-editor/apply-requested" }
  | {
      readonly type: "thread-ng-editor/write-completed";
      readonly kind: "save" | "delete";
      readonly success: boolean;
      readonly name: string;
      readonly presets: ThreadNgPreset[];
    };

export type ThreadNgWordEditorEffect =
  | { readonly type: "change-value"; readonly value: string }
  | {
      readonly type: "write-presets";
      readonly kind: "save" | "delete";
      readonly name: string;
      readonly presets: ThreadNgPreset[];
    };

export function isThreadNgWordEditorEvent(
  event: TypedEvent,
): event is ThreadNgWordEditorEvent {
  return event.type.startsWith("thread-ng-editor/");
}

export function createThreadNgWordEditorMediator(
  runEffect: (effect: ThreadNgWordEditorEffect) => void,
  initial: Pick<ThreadNgWordEditorState, "value" | "disabled" | "presets">,
): Mediator<
  ThreadNgWordEditorState,
  ThreadNgWordEditorEvent,
  ThreadNgWordEditorEffect
> {
  return createMediator<
    ThreadNgWordEditorState,
    ThreadNgWordEditorEvent,
    ThreadNgWordEditorEffect
  >(
    {
      ...initial,
      validation: validateThreadNgWords(initial.value),
      name: "",
      selectedName: "",
      storageMessage: "",
    },
    (state, event) => {
      switch (event.type) {
        case "thread-ng-editor/model-synchronized":
          return {
            status: "handled",
            state: {
              ...state,
              value: event.value,
              validation: validateThreadNgWords(event.value),
              disabled: event.disabled,
            },
          };
        case "thread-ng-editor/presets-synchronized":
          return {
            status: "handled",
            state: {
              ...state,
              presets: event.presets,
              selectedName: event.presets.some(
                (preset) => preset.name === state.selectedName,
              )
                ? state.selectedName
                : "",
            },
          };
        case "thread-ng-editor/value-changed":
          if (state.disabled) return { status: "handled", state };
          return {
            status: "handled",
            state: {
              ...state,
              value: event.value,
              validation: validateThreadNgWords(event.value),
              storageMessage: "",
            },
            effects: [{ type: "change-value", value: event.value }],
          };
        case "thread-ng-editor/name-changed":
          return {
            status: "handled",
            state: state.disabled ? state : { ...state, name: event.name },
          };
        case "thread-ng-editor/selection-changed":
          return {
            status: "handled",
            state: state.disabled
              ? state
              : { ...state, selectedName: event.name },
          };
        case "thread-ng-editor/save-requested": {
          const name = state.name.trim();
          const patterns = state.validation.patterns;
          if (state.disabled || !name || !patterns?.length)
            return { status: "handled", state };
          return {
            status: "handled",
            state: { ...state, storageMessage: "" },
            effects: [
              {
                type: "write-presets",
                kind: "save",
                name,
                presets: [
                  ...state.presets.filter((preset) => preset.name !== name),
                  { name, patterns },
                ],
              },
            ],
          };
        }
        case "thread-ng-editor/delete-requested":
          if (state.disabled || !state.selectedName)
            return { status: "handled", state };
          return {
            status: "handled",
            state: { ...state, storageMessage: "" },
            effects: [
              {
                type: "write-presets",
                kind: "delete",
                name: state.selectedName,
                presets: state.presets.filter(
                  (preset) => preset.name !== state.selectedName,
                ),
              },
            ],
          };
        case "thread-ng-editor/apply-requested": {
          if (state.disabled) return { status: "handled", state };
          const preset = state.presets.find(
            (item) => item.name === state.selectedName,
          );
          if (!preset) return { status: "handled", state };
          const value = preset.patterns.join("\n");
          return {
            status: "handled",
            state: {
              ...state,
              value,
              validation: validateThreadNgWords(value),
            },
            effects: [{ type: "change-value", value }],
          };
        }
        case "thread-ng-editor/write-completed":
          return {
            status: "handled",
            state: event.success
              ? {
                  ...state,
                  presets: event.presets,
                  selectedName: event.kind === "save" ? event.name : "",
                  storageMessage:
                    event.kind === "save"
                      ? "プリセットを保存しました。"
                      : "プリセットを削除しました。",
                }
              : {
                  ...state,
                  storageMessage:
                    event.kind === "save"
                      ? "プリセットを保存できませんでした。ブラウザの保存設定を確認してください。"
                      : "プリセットを削除できませんでした。ブラウザの保存設定を確認してください。",
                },
          };
      }
    },
    { runEffect },
  );
}
